import {
  language,
  defaultMealboxSlug,
  FILTER_CATEGORIES,
} from "@chef/constants";
import {
  getWeek,
  getYear,
  isAddonProduct,
  isLimitedQuantityProduct,
  isMealboxProduct,
  isOneSubProduct,
  isPickAndMixProduct,
  isStandaloneProduct,
  nextDeliveryWeekDateObj,
} from "@chef/helpers";
import {
  IDeviationBasket,
  IDeviationProduct,
  PickAndMixQuery,
  ProductsByCategoriesQuery,
  RootState,
} from "@chef/state-management";
import {
  getAttribute,
  getDiscountedAmount,
} from "@chef/state-management/helpers";
import { isEqualStrings } from "@chef/utils/equal";
import { isNotEmptyArray } from "@chef/utils/array";

import { usePickAndMixProductWithPrice } from "./hooks";

export const sharedIntl = (
  {
    no: {
      RECOMMENDED_DISHES: "Vi anbefaler",
      DISCOUNTED_DISHES: "Tilbud",
      DISCOUNTED_DISHES_SLUG: "tilbud",
      ALL_DISHES_TEXT: "Alle",
      SEE_YOUR_ORDER: "Vis din bestilling",
      SECOND_PAGE_HEADER_BACK: "Velg retter",
      THIRD_PAGE_HEADER_BACK: "Legg til dagligvarer",
    },

    se: {
      RECOMMENDED_DISHES: "Rekommenderade rätter",
      DISCOUNTED_DISHES: "Kampanj",
      DISCOUNTED_DISHES_SLUG: "kampanj",
      ALL_DISHES_TEXT: "Alla",
      SEE_YOUR_ORDER: "Se din beställning",
      SECOND_PAGE_HEADER_BACK: "Välj middagar",
      THIRD_PAGE_HEADER_BACK: "Lägg till livsmedel",
    },

    dk: {
      RECOMMENDED_DISHES: "Vi anbefaler",
      YOUR_DELIVERIES_ARE_STOPPED: "Dine leveringer er blevet stoppet",
      DISCOUNTED_DISHES: "Tilbud",
      DISCOUNTED_DISHES_SLUG: "tilbud",
      ALL_DISHES_TEXT: "Alle",
      SEE_YOUR_ORDER: "Se din ordre",
      SECOND_PAGE_HEADER_BACK: "Vælg retter",
      THIRD_PAGE_HEADER_BACK: "Tilføj specialvarer",
    },
  } as const
)[language];

export const getSteps = () => {
  return {
    no: [
      { title: `Velg retter` },
      { title: `Legg til dagligvarer` },
      { title: `Bekreft bestilling` },
    ],
    se: [
      { title: `Välj middagar` },
      { title: `Lägg till livsmedel` },
      { title: `Bekräfta beställning` },
    ],

    dk: [
      { title: `Vælg retter` },
      { title: "Tilføj specialvarer" },
      { title: "Bekræft ordre" },
    ],
  }[language];
};

export const insertRecommendationFilter = (
  filters: IFilterCategory[],
  position = 1,
  nRecommendations = 0,
) => {
  if (!nRecommendations) {
    return filters;
  }

  return [
    ...filters.slice(0, position),
    {
      count: nRecommendations,
      label: sharedIntl.RECOMMENDED_DISHES,
      value: FILTER_CATEGORIES.RECOMMENDATIONS_SLUG,
      special: false,
      isAll: false,
    },
    ...filters.slice(position),
  ];
};

interface IFilterCategory {
  value: string;
  label: string;
  count: number;
  type: "default" | "highlighted" | "secondary";
  isAll: boolean;
}

export const isProductByCategoriesOnDiscount = (args: {
  productsByCategory: ProductsByCategoriesQuery["productsByCategories"][0];
  productId: string;
}): boolean => {
  const { productsByCategory, productId } = args;

  const product = productsByCategory.products.find((p) =>
    isEqualStrings(p.productId, productId),
  );
  if (!product) {
    return false;
  }

  const variation = product.variations[0];

  return !!getDiscountedAmount(variation);
};

export const filterDiscountedVariations = (
  product: ProductsByCategoriesQuery["productsByCategories"][number]["products"][number],
) => ({
  ...product,
  variations: product.variations.filter((variation) =>
    getDiscountedAmount(variation),
  ),
});

const filterUniqueProducts = (
  products: ProductsByCategoriesQuery["productsByCategories"][number]["products"],
  seenProducts: Set<string>,
) => {
  return products.filter((p) => {
    if (seenProducts.has(p.productId)) {
      return false;
    }
    seenProducts.add(p.productId);
    return true;
  });
};

export const generateFilterCategoriesFromProductsByCategories = (args: {
  productsByCategories: ProductsByCategoriesQuery["productsByCategories"];
  excludeAll?: boolean;
  excludeDiscount?: boolean;
}) => {
  const { productsByCategories, excludeAll, excludeDiscount } = args;

  const generatedCategories: IFilterCategory[] = productsByCategories.map(
    (pbc) => ({
      value: pbc.slug,
      label: pbc.productCategoryName,
      count: pbc.products.length,
      type: "default",
      isAll: false,
    }),
  );

  if (!excludeDiscount) {
    const seenProducts = new Set<string>();
    const count = productsByCategories.reduce(
      (a, b) =>
        a +
        filterUniqueProducts(
          b.products.filter((p) =>
            isProductByCategoriesOnDiscount({
              productsByCategory: b,
              productId: p.productId,
            }),
          ),
          seenProducts,
        ).length,
      0,
    );

    // only add if we have discounted products
    if (count) {
      generatedCategories.unshift({
        value: sharedIntl.DISCOUNTED_DISHES_SLUG,
        label: sharedIntl.DISCOUNTED_DISHES,
        count,
        type: "highlighted",
        isAll: false,
      });
    }
  }

  if (!excludeAll) {
    generatedCategories.unshift({
      value: FILTER_CATEGORIES.ALL_CATEGORIES_SLUG,
      label: sharedIntl.ALL_DISHES_TEXT,
      count: productsByCategories.reduce((a, b) => a + b.products.length, 0),
      type: "default",
      isAll: true,
    });
  }

  return generatedCategories;
};

export const filterProductsByCategoriesFilterCategory = (args: {
  productsByCategories: ProductsByCategoriesQuery["productsByCategories"];
  filter: string;
  searchValue?: string;
}) => {
  const { productsByCategories, filter, searchValue } = args;
  const seenProducts = new Set<string>();

  if (searchValue) {
    return productsByCategories.map((pbc) => ({
      ...pbc,
      products: filterUniqueProducts(
        pbc.products.filter((p) =>
          p.name.toLowerCase().includes(searchValue.toLowerCase()),
        ),
        seenProducts,
      ),
    }));
  } else {
    if (filter === FILTER_CATEGORIES.ALL_CATEGORIES_SLUG) {
      const seenProducts = new Set<string>();

      return productsByCategories.map((category) => ({
        ...category,
        products: filterUniqueProducts(category.products, seenProducts),
      }));
    }

    const newProductsByCategories: ProductsByCategoriesQuery["productsByCategories"] =
      [];

    for (const pbc of productsByCategories) {
      if (filter === sharedIntl.DISCOUNTED_DISHES_SLUG) {
        const products = pbc.products
          .filter((p) =>
            isProductByCategoriesOnDiscount({
              productsByCategory: pbc,
              productId: p.productId,
            }),
          )
          .map(filterDiscountedVariations)
          .filter((p) => isNotEmptyArray(p.variations));

        if (products.length === 0) {
          continue;
        }

        newProductsByCategories.push({
          ...pbc,
          products: filterUniqueProducts(products, seenProducts),
        });
        continue;
      }

      if (pbc.slug === filter) {
        newProductsByCategories.push(pbc);
      }
    }

    return newProductsByCategories;
  }
};

type PickAndMixWithPrice = ReturnType<
  typeof usePickAndMixProductWithPrice
>["pickAndMix"];

export const sortPickAndMixByPrice = (args: {
  pickAndMixWithPrice: PickAndMixWithPrice;
  selectedPortions: number;
  selectedProducts: RootState["basket"]["deviations"][number]["products"];
}) => {
  const { pickAndMixWithPrice, selectedPortions, selectedProducts } = args;

  return [...pickAndMixWithPrice].sort((a, b) => {
    const findVariation = (product: PickAndMixQuery["pickAndMix"][number]) =>
      product.product.variations.find(
        (variation) =>
          +(getAttribute("Portions", variation) || 0) === selectedPortions,
      );

    const aVariation = findVariation(a);
    const bVariation = findVariation(b);

    if (!aVariation) {
      console.error("Variation not found", {
        productId: a.productId,
        product: a.product,
        selectedPortions,
      });
      return 0;
    }

    if (!bVariation) {
      console.error("Variation not found", {
        productId: b.productId,
        product: b.product,
        selectedPortions,
      });
      return 0;
    }

    const aPrice = a.recipes.find(() => true)?.price || 0;
    const bPrice = b.recipes.find(() => true)?.price || 0;

    // If we have selected products, we want to show them first
    const aIsSelected = selectedProducts.find(
      (selectedProduct) => selectedProduct.productId === a.productId,
    );

    const bIsSelected = selectedProducts.find(
      (selectedProduct) => selectedProduct.productId === b.productId,
    );

    if (aIsSelected) {
      return -1;
    }

    if (bIsSelected) {
      return 1;
    }

    // Zero extra price first
    if (aPrice === 0 && bPrice === 0) {
      return 0;
    }

    if (aPrice === 0) {
      return -1;
    }

    if (bPrice === 0) {
      return 1;
    }

    // Low to high
    return aPrice - bPrice;
  });
};

export const getDataFromPublicWeekEditorSlug = (slug: string[]) => {
  const currentDate = nextDeliveryWeekDateObj();
  let week = getWeek(currentDate);
  let year = getYear(currentDate);
  let mealboxSlug = defaultMealboxSlug as string;

  // override defaults if we have them selected
  if (slug.length === 1) {
    mealboxSlug = slug[0];
  } else if (slug.length === 2) {
    week = +slug[0];
    year = +slug[1];
  } else if (slug.length === 3) {
    mealboxSlug = slug[0];
    week = +slug[1];
    year = +slug[2];
  }

  return { week, year, mealboxSlug };
};

export const getTotalProductCount = (
  selectedProducts: IDeviationBasket["products"],
) => {
  const count = selectedProducts.reduce((sum, product) => {
    if (isOneSubProduct(product)) {
      return sum;
    }

    return sum + product.quantity;
  }, 0);

  return count;
};

export const filterBasketItems = (items: IDeviationProduct[]) => {
  const mealboxItems = items.filter(
    (bp) => isPickAndMixProduct(bp) || isMealboxProduct(bp),
  );

  const groceryItems = items.filter(
    (bp) =>
      isStandaloneProduct(bp) ||
      isAddonProduct(bp) ||
      isLimitedQuantityProduct(bp),
  );

  return {
    mealboxBasketItems: mealboxItems,
    groceryBasketItems: groceryItems,
  };
};
